export type HeritagePreAuthFormValues = {
  policyNumber?: string;
  cardNumber?: any;
  empId?: string;
  patientMobile?: any;
  mrn?: any;
  estimatedCost?: string | number | null;
  roomIcuTariff?: string | null;
  noOfDays?: string | number | null;
  roomRent?: string | number | null;
  mouDiscount?: string | number | null;
  dischargeRequest?: boolean | string | null;
  generalRoomCharge?: string | number | null;
  generalRoomDays?: string | number | null;
  icuCharge?: string | number | null;
  icuDays?: string | number | null;
  investigationCharge?: string | number | null;
  doctorCharge?: string | number | null;
  medicineCharge?: string | number | null;
  packageRate?: string | number | null;
  implantForPpnProcedure?: string | number | null;
  implantForNonPpnProcedure?: string | number | null;
  noOfStent?: string | number | null;
  stentCost?: string | number | null;
  otherCharges?: string | number | null;
  revertReason?: string | null;
  hospitalReply?: string | null;
  patientName?: string;
  admissionDate?: string | null;
  dischargeDate?: string | null;
  stayInHospitalDays?: number | string | null;
  diagnosisAilment?: string;
  claimedAmount?: number | string | null;
  treatingDoctorName?: string;
  doctorRegistrationId?: string;
  doctorMobileNo?: string;
  admissionType?: string;
  documentsList?: any[];
};

const toStr = (value: string | number | boolean | null | undefined): string | null => {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
};

const legacyStrOrNum = (value: unknown): string | number | null | undefined => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string" || typeof value === "number") return value;
  return null;
};

export const formatHeritageDischargeRequest = (
  value?: boolean | string | null
): string | null => {
  if (value === true || value === "Yes") return "Yes";
  if (value === false || value === "No") return "No";
  return null;
};

/** Maps Heritage form values to case_pre_auth_others request (DB columns). */
export const buildHeritageCasePreAuthOthersRequest = (
  formValues: HeritagePreAuthFormValues
) => {
  const estimatedAmount =
    formValues?.estimatedCost != null
      ? String(formValues.estimatedCost)
      : formValues?.claimedAmount != null
        ? String(formValues.claimedAmount)
        : null;

  const icuDays = toStr(formValues?.icuDays);

  return {
    admissionDate: formValues?.admissionDate || null,
    dischargeDate: formValues?.dischargeDate || null,
    admissionType: formValues?.admissionType || null,
    diagnosis: formValues?.diagnosisAilment || null,
    doctorName: formValues?.treatingDoctorName || null,
    doctorRegistrationId: formValues?.doctorRegistrationId || null,
    doctorMobileNo: formValues?.doctorMobileNo || null,
    policyNumber: toStr(formValues?.policyNumber),
    insuredCardNumber: toStr(formValues?.cardNumber),
    doctorContact: toStr(formValues?.patientMobile),
    requestedAmount: estimatedAmount,
    lengthOfStay: toStr(
      formValues?.noOfDays != null
        ? formValues.noOfDays
        : formValues?.stayInHospitalDays
    ),
    roomCategory: formValues?.roomIcuTariff || null,
    mrn: toStr(formValues?.mrn),
    empId: toStr(formValues?.empId),
    mouDiscount: toStr(formValues?.mouDiscount),
    dischargeRequest: formatHeritageDischargeRequest(formValues?.dischargeRequest),
    generalRoomCharge: toStr(formValues?.generalRoomCharge),
    noOfDaysInGeneralRoomCharge: toStr(formValues?.generalRoomDays),
    icuCharge: toStr(formValues?.icuCharge),
    daysInIcu: icuDays,
    investigationCharge: toStr(formValues?.investigationCharge),
    roomRent: toStr(formValues?.roomRent),
    doctorCharge: toStr(formValues?.doctorCharge),
    medicineCharge: toStr(formValues?.medicineCharge),
    packageRate: toStr(formValues?.packageRate),
    implantForPpn: toStr(formValues?.implantForPpnProcedure),
    implantForNonPpn: toStr(formValues?.implantForNonPpnProcedure),
    noOfStent: toStr(formValues?.noOfStent),
    stentCost: toStr(formValues?.stentCost),
    otherCharge: toStr(formValues?.otherCharges),
    acceptedRevertReason: formValues?.revertReason || null,
    hospitalReply: formValues?.hospitalReply || null,
    noOfDaysAndUnits: null,
  };
};

/** Maps API case_pre_auth_others (incl. legacy JSON) to Heritage form values. */
export const mapHeritagePreAuthOthersToForm = (
  others: any
): HeritagePreAuthFormValues => {
  if (!others) return {};

  let legacy: Record<string, unknown> = {};
  if (others?.noOfDaysAndUnits?.trim?.()?.startsWith("{")) {
    try {
      legacy = JSON.parse(others.noOfDaysAndUnits);
    } catch {
      legacy = {};
    }
  }

  const legacyDischarge = legacy.dischargeRequest;
  const dischargeRequest =
    others?.dischargeRequest === "Yes"
      ? true
      : others?.dischargeRequest === "No"
        ? false
        : legacyDischarge === true || legacyDischarge === "Yes";

  return {
    policyNumber: others?.policyNumber ?? (legacy.policyNumber as string | undefined),
    cardNumber: others?.insuredCardNumber ?? legacy.cardNumber,
    empId: others?.empId ?? others?.ichiCode,
    mrn: others?.mrn ?? legacyStrOrNum(legacy.mrn),
    patientMobile: others?.doctorContact ?? legacy.patientMobile,
    admissionDate: others?.admissionDate,
    dischargeDate: (others?.dischargeDate ?? legacy.dischargeDate) as string | null | undefined,
    diagnosisAilment: others?.diagnosis,
    treatingDoctorName: others?.doctorName,
    doctorRegistrationId: others?.doctorRegistrationId,
    doctorMobileNo: others?.doctorMobileNo,
    admissionType: others?.admissionType,
    estimatedCost: legacyStrOrNum(others?.requestedAmount ?? legacy.estimatedCost) ?? null,
    roomIcuTariff: (others?.roomCategory ??
      others?.speciality ??
      legacy.roomIcuTariff) as string | null | undefined,
    noOfDays: legacyStrOrNum(others?.lengthOfStay ?? legacy.noOfDays),
    dischargeRequest,
    roomRent: legacyStrOrNum(others?.roomRent ?? legacy.roomRent) ?? null,
    mouDiscount: legacyStrOrNum(others?.mouDiscount ?? legacy.mouDiscount) ?? null,
    generalRoomCharge:
      legacyStrOrNum(others?.generalRoomCharge ?? legacy.generalRoomCharge) ?? null,
    generalRoomDays:
      legacyStrOrNum(others?.noOfDaysInGeneralRoomCharge ?? legacy.generalRoomDays) ?? null,
    icuCharge: legacyStrOrNum(others?.icuCharge ?? legacy.icuCharge) ?? null,
    icuDays: legacyStrOrNum(others?.daysInIcu ?? legacy.icuDays) ?? null,
    investigationCharge:
      legacyStrOrNum(others?.investigationCharge ?? legacy.investigationCharge) ?? null,
    doctorCharge: legacyStrOrNum(others?.doctorCharge ?? legacy.doctorCharge) ?? null,
    medicineCharge: legacyStrOrNum(others?.medicineCharge ?? legacy.medicineCharge) ?? null,
    packageRate:
      legacyStrOrNum(others?.packageRate ?? others?.procedure ?? legacy.packageRate) ?? null,
    implantForPpnProcedure:
      legacyStrOrNum(others?.implantForPpn ?? legacy.implantForPpnProcedure) ?? null,
    implantForNonPpnProcedure:
      legacyStrOrNum(others?.implantForNonPpn ?? legacy.implantForNonPpnProcedure) ?? null,
    noOfStent: legacyStrOrNum(others?.noOfStent ?? legacy.noOfStent) ?? null,
    stentCost: legacyStrOrNum(others?.stentCost ?? legacy.stentCost) ?? null,
    otherCharges: legacyStrOrNum(others?.otherCharge ?? legacy.otherCharges) ?? null,
    revertReason: (others?.acceptedRevertReason ?? legacy.revertReason ?? null) as string | null,
    hospitalReply: (others?.hospitalReply ?? legacy.hospitalReply ?? null) as string | null,
  };
};
